import React, { useState } from 'react';
import { Plus, Trash2, Archive, MessageSquare } from 'lucide-react';
import { deleteSession, updateSession } from '../../services/insightLabService';

const SessionSidebar = ({ sessions, activeSessionId, onSelectSession, onNewSession, onSessionsChange, isCreating }) => {
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (e, sessionId) => {
    e.stopPropagation();
    if (deletingId) return;
    setDeletingId(sessionId);
    try {
      await deleteSession(sessionId);
      onSessionsChange();
    } catch (err) {
      console.error('Failed to delete session:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleArchive = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await updateSession(sessionId, { status: 'archived' });
      onSessionsChange();
    } catch (err) {
      console.error('Failed to archive session:', err);
    }
  };

  const activeSessions = sessions.filter(s => s.status === 'active');
  const archivedSessions = sessions.filter(s => s.status === 'archived');

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderSession = (session) => {
    const isActive = session.id === activeSessionId;
    return (
      <div
        key={session.id}
        onClick={() => onSelectSession(session.id)}
        className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
          isActive
            ? 'bg-primary/10 text-primary border border-primary/20'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
        }`}
      >
        <MessageSquare className="w-4 h-4 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{session.title || 'New Session'}</div>
          <div className="text-xs text-muted-foreground">{formatDate(session.updated_at)}</div>
        </div>
        <div className="hidden group-hover:flex items-center gap-1">
          {session.status === 'active' && (
            <button
              onClick={(e) => handleArchive(e, session.id)}
              className="p-1 rounded hover:bg-accent/80 text-muted-foreground hover:text-foreground"
              title="Archive"
            >
              <Archive className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => handleDelete(e, session.id)}
            className="p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
            title="Delete"
            disabled={deletingId === session.id}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-card border-r border-border">
      <div className="p-3 border-b border-border">
        <button
          onClick={onNewSession}
          disabled={isCreating}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors font-medium text-sm"
        >
          <Plus className="w-4 h-4" />
          {isCreating ? 'Starting...' : 'New Session'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {activeSessions.length === 0 && archivedSessions.length === 0 && (
          <p className="text-center text-muted-foreground text-sm py-8 px-4">
            No sessions yet. Start a new conversation!
          </p>
        )}

        {activeSessions.map(renderSession)}

        {archivedSessions.length > 0 && (
          <>
            <div className="pt-4 pb-1 px-3">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Archived</span>
            </div>
            {archivedSessions.map(renderSession)}
          </>
        )}
      </div>
    </div>
  );
};

export default SessionSidebar;
